import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaSpinner, FaArrowLeft } from 'react-icons/fa';
import PublicLayout from '../../components/PublicLayout';
import '../../styles/Auth.css';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

  const handleShowToast = (message, type = 'error') => {
    setToast({ open: true, message, type });
    setTimeout(() => {
      setToast({ open: false, message: '', type: 'error' });
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const API_URL = process.env.NODE_ENV === 'development' 
        ? '/api' 
        : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');
      
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        if (response.status === 503) {
          handleShowToast('Service temporarily unavailable. Please try again later.');
        } else {
          handleShowToast('An error occurred. Please try again.');
        }
        return;
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setEmailSent(true);
      } else {
        handleShowToast(data.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password request failed:', err);
      handleShowToast('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <PublicLayout>
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                backgroundColor: '#d1fae5',
                borderRadius: '50%',
                margin: '0 auto 20px',
                fontSize: '32px',
                color: '#10b981'
              }}>
                ✓
              </div>
              <h1>Check Your Email</h1>
              <p>We've sent a password reset link to <strong>{email}</strong></p>
            </div>
            
            <div style={{ 
              backgroundColor: '#f0f9ff', 
              border: '1px solid #3b9efc', 
              borderRadius: '8px', 
              padding: '20px', 
              margin: '20px 0',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#1e40af', margin: '0 0 10px 0', fontSize: '16px' }}>
                Next Steps:
              </h3>
              <p style={{ color: '#374151', margin: '0', fontSize: '14px', lineHeight: '1.5' }}>
                1. Check your email inbox (and spam folder)<br/>
                2. Click the password reset link<br/>
                3. Create a new password
              </p>
            </div>

            <div style={{ 
              backgroundColor: '#fef3c7', 
              border: '1px solid #fbbf24', 
              borderRadius: '8px', 
              padding: '15px', 
              margin: '20px 0'
            }}>
              <p style={{ color: '#92400e', margin: '0', fontSize: '14px', textAlign: 'center' }}>
                <strong>Note:</strong> The reset link expires in 10 minutes for security reasons.
              </p>
            </div>

            <div className="auth-footer">
              <p>
                <Link to="/login" style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  color: '#3b9efc', 
                  textDecoration: 'none',
                  fontSize: '14px'
                }}>
                  <FaArrowLeft style={{ marginRight: '8px' }} />
                  Back to Login
                </Link>
              </p>
              <p style={{ marginTop: '15px', fontSize: '14px', color: '#6b7280' }}>
                Didn't receive the email? Check your spam folder or{' '}
                <button 
                  onClick={() => setEmailSent(false)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#3b9efc', 
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px'
                  }}
                >
                  try again
                </button>
              </p>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Your Password</h1>
            <p>Enter your email address and we'll send you a link to reset your password</p>
          </div>
          
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-with-icon">
                <FaEnvelope className="input-icon" />
                <input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  aria-invalid={toast.open ? 'true' : 'false'}
                />
              </div>
            </div>
            
            <button 
              type="submit"
              className="auth-button"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '16px' }} />
                  Sending Reset Link...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
          
          <div className="auth-footer">
            <p>
              <Link to="/login" style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                color: '#3b9efc', 
                textDecoration: 'none',
                fontSize: '14px'
              }}>
                <FaArrowLeft style={{ marginRight: '8px' }} />
                Back to Login
              </Link>
            </p>
          </div>

          {/* Toast Messages */}
          {toast.open && (
            <div 
              className="login-toast-error" 
              style={{ 
                border: toast.type === 'success' ? '1px solid #10b981' : '1px solid #e53935', 
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)', 
                alignItems: 'center', 
                borderRadius: '8px', 
                position: 'relative',
                backgroundColor: toast.type === 'success' ? '#f0fdf4' : '#fef2f2'
              }}
            >
              <button 
                onClick={() => setToast({ open: false, message: '', type: 'error' })}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: toast.type === 'success' ? '#10b981' : '#e53935',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                ×
              </button>
              <div className="login-toast-content">
                <div 
                  className="login-toast-title" 
                  style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    marginBottom: '2px', 
                    color: '#222', 
                    paddingLeft: '10px'
                  }}
                >
                  {toast.type === 'success' ? ' Success' : '❗ Error'}
                </div>
                <div 
                  className="login-toast-message" 
                  style={{ 
                    fontSize: '0.75rem', 
                    color: toast.type === 'success' ? '#10b981' : '#e53935', 
                    paddingLeft: '10px', 
                    paddingRight: '24px'
                  }}
                >
                  {toast.message}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
